/**
 * `window.websim.ai` and `window.websim.agent`: the in-page agent surface.
 *
 * `await websim.ai()` lazy-loads the `@websim/ai` bundle (the Vercel AI SDK
 * plus a websim model provider, built from packages/websim-ai and served from
 * the API origin) and returns it; `websim.ai.model()` is the project's
 * platform-assigned model. `websim.agent.create()` is sugar over the SDK's
 * `ToolLoopAgent`. Nothing here runs a model: every call the SDK makes goes
 * through `bridgeFetch`, which hands the OpenAI-shaped request to the parent
 * frame, and the parent posts it to party's metered endpoint.
 *
 * Images: the SDK gives us OpenAI `image_url` parts by the time the request
 * reaches the bridge. Anything the browser can decode is re-encoded here as a
 * bounded JPEG data URL (blob: URLs, same-origin and CORS-readable http(s)
 * URLs, WebP/AVIF/SVG/HEIC where the browser supports them); what it cannot
 * fetch is forwarded for the server to fetch.
 */
import type { ParentApi } from "@websim/core/src/parent-api/types";
import type { Stub } from "@websim/message-proxy/client";
import type * as WebsimAiSdk from "@websim/ai";

type ParentApiStub = Stub<ParentApi>;

export type WebsimAiModule = typeof WebsimAiSdk;

/** Longest side after page-side normalization; matches the server's bound. */
const MAX_IMAGE_DIMENSION = 1024;
/** Data URLs at or under this size in an accepted type pass through untouched. */
const PASSTHROUGH_MAX_BYTES = 1024 * 1024;
const PASSTHROUGH_TYPES = new Set(["image/jpeg", "image/png"]);

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

async function decodeToCanvas(blob: Blob): Promise<HTMLCanvasElement> {
  let width: number;
  let height: number;
  let source: CanvasImageSource;
  let release: () => void = () => {};
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob);
      width = bitmap.width;
      height = bitmap.height;
      source = bitmap;
      release = () => bitmap.close();
    } catch {
      ({ width, height, source, release } = await decodeViaImage(blob));
    }
  } else {
    ({ width, height, source, release } = await decodeViaImage(blob));
  }
  try {
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height, 1));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("canvas 2d context unavailable");
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    release();
  }
}

function decodeViaImage(blob: Blob): Promise<{
  width: number;
  height: number;
  source: CanvasImageSource;
  release: () => void;
}> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () =>
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
        source: image,
        release: () => URL.revokeObjectURL(url),
      });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image could not be decoded"));
    };
    image.src = url;
  });
}

function hasAlpha(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext("2d");
  if (!context) return false;
  // Sample a grid rather than every pixel: this runs on the hot path of a
  // vision call and a 1024² readback is fine, but scanning 4M bytes is not.
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  const step = Math.max(4, Math.floor(data.length / 4 / 4096) * 4);
  for (let i = 3; i < data.length; i += step) {
    if (data[i] < 255) return true;
  }
  return false;
}

/** Any image-ish value → a bounded JPEG/PNG data URL, or null if undecodable here. */
export async function imageToDataUrl(
  input:
    | Blob
    | ArrayBuffer
    | ArrayBufferView
    | HTMLCanvasElement
    | HTMLImageElement
    | ImageBitmap
    | string
): Promise<string | null> {
  try {
    let canvas: HTMLCanvasElement;
    if (typeof input === "string") {
      if (input.startsWith("data:")) {
        const header = input.slice(5, input.indexOf(","));
        const mime = header.split(";")[0].toLowerCase();
        const approxBytes = Math.floor((input.length - header.length) * 0.75);
        if (PASSTHROUGH_TYPES.has(mime) && approxBytes <= PASSTHROUGH_MAX_BYTES) {
          return input;
        }
      }
      // blob:, data: in another format, or an http(s) URL the page can read.
      const response = await fetch(input, { mode: "cors" });
      if (!response.ok) return null;
      canvas = await decodeToCanvas(await response.blob());
    } else if (input instanceof Blob) {
      canvas = await decodeToCanvas(input);
    } else if (input instanceof ArrayBuffer || ArrayBuffer.isView(input)) {
      canvas = await decodeToCanvas(new Blob([input as BlobPart]));
    } else if (typeof HTMLCanvasElement !== "undefined" && input instanceof HTMLCanvasElement) {
      canvas = input;
      if (Math.max(canvas.width, canvas.height) > MAX_IMAGE_DIMENSION) {
        const scaled = document.createElement("canvas");
        const scale = MAX_IMAGE_DIMENSION / Math.max(canvas.width, canvas.height);
        scaled.width = Math.round(canvas.width * scale);
        scaled.height = Math.round(canvas.height * scale);
        scaled.getContext("2d")?.drawImage(canvas, 0, 0, scaled.width, scaled.height);
        canvas = scaled;
      }
    } else {
      // HTMLImageElement or ImageBitmap
      const source = input as CanvasImageSource & { width: number; height: number };
      const width =
        "naturalWidth" in source ? (source as HTMLImageElement).naturalWidth : source.width;
      const height =
        "naturalHeight" in source ? (source as HTMLImageElement).naturalHeight : source.height;
      const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height, 1));
      canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      canvas.getContext("2d")?.drawImage(source, 0, 0, canvas.width, canvas.height);
    }
    return hasAlpha(canvas)
      ? canvas.toDataURL("image/png")
      : canvas.toDataURL("image/jpeg", 0.85);
  } catch {
    return null;
  }
}

type Part = Record<string, unknown>;

function partImageUrl(part: Part): string | null {
  if (part.type === "image_url") {
    const value = part.image_url;
    if (typeof value === "string") return value;
    const url = (value as { url?: unknown } | undefined)?.url;
    return typeof url === "string" ? url : null;
  }
  if (part.type === "image") {
    if (typeof part.image === "string") return part.image;
    const source = part.source as { type?: unknown; media_type?: unknown; data?: unknown } | undefined;
    if (source?.type === "base64" && typeof source.data === "string") {
      return `data:${typeof source.media_type === "string" ? source.media_type : "image/jpeg"};base64,${source.data}`;
    }
  }
  return null;
}

/**
 * Rewrite image parts in an OpenAI-shaped `messages` array in place so every
 * image the browser can decode reaches the server as a bounded data URL.
 * Sloppy inputs (a Blob, a canvas, an <img>, an ImageBitmap in place of a
 * URL) are accepted too. Undecodable-here sources are left for the server.
 */
export async function normalizeMessageImagesInPage(messages: unknown): Promise<void> {
  if (!Array.isArray(messages)) return;
  for (const message of messages) {
    const content = (message as { content?: unknown } | null)?.content;
    if (!Array.isArray(content)) continue;
    for (let i = 0; i < content.length; i++) {
      const part = content[i];
      if (!part || typeof part !== "object") continue;
      const p = part as Part;
      const raw =
        p.type === "image_url" || p.type === "image" || p.type === "input_image"
          ? (p.image_url as unknown) ?? p.image ?? p.source
          : null;
      let normalized: string | null = null;
      if (
        raw instanceof Blob ||
        (typeof HTMLCanvasElement !== "undefined" && raw instanceof HTMLCanvasElement) ||
        (typeof HTMLImageElement !== "undefined" && raw instanceof HTMLImageElement) ||
        (typeof ImageBitmap !== "undefined" && raw instanceof ImageBitmap)
      ) {
        normalized = await imageToDataUrl(raw);
        if (!normalized) continue;
      } else {
        const url = p.type === "input_image" && typeof p.image_url === "string" ? p.image_url : partImageUrl(p);
        if (!url) continue;
        if (url.startsWith("data:") || url.startsWith("blob:") || /^https?:/.test(url)) {
          normalized = await imageToDataUrl(url);
        }
        if (!normalized) {
          if (url.startsWith("blob:")) continue; // dropped: nothing else can read it
          normalized = url; // the server fetches http(s) it could not read here
        }
      }
      const detail = (p.image_url as { detail?: unknown } | undefined)?.detail;
      content[i] = {
        type: "image_url",
        image_url: { url: normalized, ...(typeof detail === "string" ? { detail } : {}) },
      };
    }
  }
}

type ImageConverter = (input: Parameters<typeof imageToDataUrl>[0]) => Promise<string | null>;

function isBrowserImageValue(value: unknown): value is Parameters<typeof imageToDataUrl>[0] {
  return (
    (typeof Blob !== "undefined" && value instanceof Blob) ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value) ||
    (typeof HTMLCanvasElement !== "undefined" && value instanceof HTMLCanvasElement) ||
    (typeof HTMLImageElement !== "undefined" && value instanceof HTMLImageElement) ||
    (typeof ImageBitmap !== "undefined" && value instanceof ImageBitmap)
  );
}

/**
 * Normalize AI SDK `{ type: "image", image: ... }` parts before the SDK
 * validates its prompt. This deliberately preserves the AI SDK shape; the
 * later bridge step converts the provider request to OpenAI `image_url`.
 */
export async function normalizeAiSdkMessagesInPage(
  messages: unknown,
  convert: ImageConverter = imageToDataUrl
): Promise<void> {
  if (!Array.isArray(messages)) return;
  for (const message of messages) {
    const content = (message as { content?: unknown } | null)?.content;
    if (!Array.isArray(content)) continue;
    for (let i = 0; i < content.length; i++) {
      const part = content[i];
      if (!part || typeof part !== "object") continue;
      const imagePart = part as Part;
      if (imagePart.type !== "image" || !isBrowserImageValue(imagePart.image)) {
        continue;
      }
      const normalized = await convert(imagePart.image);
      if (normalized) content[i] = { ...imagePart, image: normalized };
    }
  }
}

/** Copy only the prompt spine so conversion never mutates application state. */
export async function normalizeAiSdkCallOptions<T>(
  options: T,
  convert: ImageConverter = imageToDataUrl
): Promise<T> {
  if (!options || typeof options !== "object") return options;
  const normalized = { ...(options as Record<string, unknown>) };
  for (const key of ["messages", "prompt"] as const) {
    const messages = normalized[key];
    if (!Array.isArray(messages)) continue;
    const copy = messages.map((message) => {
      if (!message || typeof message !== "object") return message;
      const cloned = { ...(message as Record<string, unknown>) };
      if (Array.isArray(cloned.content)) {
        cloned.content = cloned.content.map((part) =>
          part && typeof part === "object"
            ? { ...(part as Record<string, unknown>) }
            : part
        );
      }
      return cloned;
    });
    await normalizeAiSdkMessagesInPage(copy, convert);
    normalized[key] = copy;
  }
  return normalized as T;
}

// ---------------------------------------------------------------------------
// Bridge
// ---------------------------------------------------------------------------

export class WebsimAiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string | null
  ) {
    super(message);
    this.name = "WebsimAiError";
  }
}

function abortError(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted", "AbortError");
}

function cancellableBridgeStream(
  source: ReadableStream<Uint8Array<ArrayBuffer>>,
  signal: AbortSignal | null | undefined,
  cancelRemote: () => Promise<void>
): ReadableStream<Uint8Array<ArrayBuffer>> {
  const reader = source.getReader();
  let controller: ReadableStreamDefaultController<Uint8Array<ArrayBuffer>> | null = null;
  let ended = false;
  const finish = () => {
    if (ended) return;
    ended = true;
    signal?.removeEventListener("abort", onAbort);
  };
  const onAbort = () => {
    if (ended || !signal) return;
    const reason = abortError(signal);
    void cancelRemote();
    void reader.cancel(reason).catch(() => {});
    controller?.error(reason);
    finish();
  };

  const stream = new ReadableStream<Uint8Array<ArrayBuffer>>({
    start(nextController) {
      controller = nextController;
      signal?.addEventListener("abort", onAbort, { once: true });
      if (signal?.aborted) onAbort();
    },
    async pull(nextController) {
      if (ended) return;
      try {
        const { value, done } = await reader.read();
        if (ended) return;
        if (done) {
          nextController.close();
          finish();
        } else {
          nextController.enqueue(value);
        }
      } catch (error) {
        if (!ended) nextController.error(error);
        finish();
      }
    },
    async cancel(reason) {
      if (ended) return;
      try {
        await Promise.allSettled([reader.cancel(reason), cancelRemote()]);
      } finally {
        finish();
      }
    },
  });
  return stream;
}

/** Execute one bridge request and keep its host fetch tied to local disposal. */
export async function requestAgentChatCompletion(
  parentApi: ParentApiStub,
  request: Record<string, unknown>,
  signal?: AbortSignal | null
): Promise<Response> {
  if (signal?.aborted) throw abortError(signal);

  const requestId = crypto.randomUUID();
  let cancellation: Promise<void> | null = null;
  const cancelRemote = () => {
    cancellation ??= Promise.resolve(
      parentApi.cancelAgentChatCompletion({ requestId })
    ).catch(() => {});
    return cancellation;
  };
  const cancelWhileConnecting = () => void cancelRemote();
  signal?.addEventListener("abort", cancelWhileConnecting, { once: true });

  let result: Awaited<ReturnType<ParentApiStub["agentChatCompletion"]>>;
  try {
    result = await parentApi.agentChatCompletion({ request, requestId });
  } catch (error) {
    signal?.removeEventListener("abort", cancelWhileConnecting);
    if (signal?.aborted) throw abortError(signal);
    throw error;
  }
  signal?.removeEventListener("abort", cancelWhileConnecting);

  if (signal?.aborted) {
    void result.stream?.cancel(abortError(signal));
    await cancelRemote();
    throw abortError(signal);
  }

  const headers = new Headers(result.headers);
  headers.set(
    "content-type",
    result.status === 200 && request.stream === true
      ? "text/event-stream"
      : "application/json"
  );
  const body = result.stream
    ? cancellableBridgeStream(result.stream, signal, cancelRemote)
    : null;
  return new Response(body, { status: result.status, headers });
}

/**
 * The parent-frame bridge as a `fetch`: the body is an OpenAI chat-completions
 * request; the parent adds the project and posts it to party. The Response
 * carries party's status and bytes; the content type is re-stated from the
 * request shape so the SDK sees exactly JSON or SSE whatever the hop added.
 */
export function createBridgeFetch(parentApi: ParentApiStub): typeof fetch {
  return async (_input, init) => {
    const raw = init?.body;
    if (typeof raw !== "string") {
      throw new WebsimAiError("websim.ai only supports JSON request bodies", 400, null);
    }
    let request: Record<string, unknown>;
    try {
      request = JSON.parse(raw);
    } catch {
      throw new WebsimAiError("websim.ai request body is not JSON", 400, null);
    }
    await normalizeMessageImagesInPage(request.messages);
    return requestAgentChatCompletion(parentApi, request, init?.signal);
  };
}

/** Read party's error envelope out of a non-2xx bridge Response. */
export async function bridgeError(response: Response): Promise<WebsimAiError> {
  let message = `Request failed (HTTP ${response.status})`;
  let code: string | null = null;
  try {
    const body = (await response.json()) as { error?: { message?: string; code?: string } };
    if (body?.error?.message) message = body.error.message;
    if (body?.error?.code) code = body.error.code;
  } catch {
    // keep the default
  }
  return new WebsimAiError(message, response.status, code);
}

// ---------------------------------------------------------------------------
// window.websim.ai / window.websim.agent
// ---------------------------------------------------------------------------

/** A tool as project code writes it: plain JSON schema, plain function. */
export interface WebsimAgentToolSpec {
  description?: string;
  /** JSON schema for the arguments (or an AI SDK / zod schema, passed through). */
  parameters?: unknown;
  /** Alias of `parameters`, matching the AI SDK's name. */
  inputSchema?: unknown;
  execute?: (args: any) => unknown | Promise<unknown>;
}

export interface WebsimModelOptions {
  /**
   * Whether the model thinks before each step (default: on). Off trades the
   * step's chain of thought for a faster first token.
   */
  reasoning?: boolean;
}

export interface WebsimAgentCreateOptions extends WebsimModelOptions {
  /** The persona / system prompt. `system` is accepted as an alias. */
  instructions?: string;
  system?: string;
  tools?: Record<string, WebsimAgentToolSpec>;
  /** Tool-call steps per `generate`/`stream` before the loop stops (default 6). */
  maxSteps?: number;
  /** Any other `ToolLoopAgent` option is passed through. */
  [option: string]: unknown;
}

export type WebsimAi = (() => Promise<WebsimAiModule>) & {
  /** The project's platform-assigned model. Available after `await websim.ai()`. */
  model: (options?: WebsimModelOptions) => WebsimAiSdk.LanguageModel;
};

export interface WebsimAgentApi {
  create: (options?: WebsimAgentCreateOptions) => Promise<WebsimAiSdk.ToolLoopAgent<any, any, any>>;
}

const DEFAULT_MAX_STEPS = 6;

function wrapToolLoopAgent<T extends object>(agent: T): T {
  return new Proxy(agent, {
    get(target, property) {
      const value = Reflect.get(target, property, target);
      if (
        (property === "generate" || property === "stream") &&
        typeof value === "function"
      ) {
        return async (callOptions: unknown) =>
          Reflect.apply(value, target, [
            await normalizeAiSdkCallOptions(callOptions),
          ]);
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

/** Wrap the SDK constructor too, not only the `websim.agent.create` sugar. */
function withBrowserToolLoopAgent(mod: WebsimAiModule): WebsimAiModule {
  const ToolLoopAgent = new Proxy(mod.ToolLoopAgent, {
    construct(Target, args, newTarget) {
      return wrapToolLoopAgent(Reflect.construct(Target, args, newTarget));
    },
  });
  return { ...mod, ToolLoopAgent } as WebsimAiModule;
}

function isJsonSchemaObject(schema: unknown): schema is Record<string, unknown> {
  if (!schema || typeof schema !== "object") return false;
  // zod (v3/v4) and AI SDK `jsonSchema()` values carry these markers; a plain
  // object with `type`/`properties` is a raw JSON schema the SDK must wrap.
  const s = schema as Record<string, unknown>;
  if ("~standard" in s || "_def" in s || "_zod" in s) return false;
  if (typeof s.jsonSchema === "object" && s.jsonSchema !== null) return false;
  return typeof s.type === "string" || "properties" in s;
}

export function createAiRuntime(parentApi: ParentApiStub): {
  ai: WebsimAi;
  agent: WebsimAgentApi;
} {
  let modulePromise: Promise<WebsimAiModule> | null = null;
  let loaded: WebsimAiModule | null = null;
  // One model instance per reasoning setting: the SDK treats the instance as
  // the identity of "which model", so two agents with different settings
  // share nothing but the bridge.
  const models = new Map<string, WebsimAiSdk.LanguageModel>();
  const bridgeFetch = createBridgeFetch(parentApi);

  const load = (): Promise<WebsimAiModule> => {
    modulePromise ??= (async () => {
      const url = await parentApi.getAiSdkUrl();
      // A URL string keeps bundlers from trying to resolve this at build time.
      const imported = (await import(/* @vite-ignore */ /* webpackIgnore: true */ url)) as WebsimAiModule;
      const mod = withBrowserToolLoopAgent(imported);
      loaded = mod;
      return mod;
    })().catch((error) => {
      modulePromise = null;
      throw error;
    });
    return modulePromise;
  };

  const ai = Object.assign(() => load(), {
    model: (options: WebsimModelOptions = {}) => {
      if (!loaded) {
        throw new Error(
          "websim.ai.model() is available after `await websim.ai()` — call that first"
        );
      }
      const key = options.reasoning === undefined ? "default" : `reasoning:${options.reasoning}`;
      let model = models.get(key);
      if (!model) {
        model = loaded.createWebsimModel({
          fetch: bridgeFetch,
          ...(options.reasoning === undefined ? {} : { reasoning: options.reasoning }),
        });
        models.set(key, model);
      }
      return model;
    },
  }) as WebsimAi;

  const agent: WebsimAgentApi = {
    create: async (options = {}) => {
      const mod = await load();
      const { instructions, system, tools, maxSteps, reasoning, ...rest } = options;
      const wrappedTools: Record<string, unknown> = {};
      for (const [name, spec] of Object.entries(tools ?? {})) {
        const schema = spec.inputSchema ?? spec.parameters ?? { type: "object", properties: {} };
        wrappedTools[name] = mod.tool({
          description: spec.description,
          inputSchema: (isJsonSchemaObject(schema) ? mod.jsonSchema(schema) : schema) as never,
          ...(spec.execute
            ? {
                execute: async (args: unknown) => {
                  try {
                    const result = await spec.execute!(args);
                    return result === undefined ? { ok: true } : result;
                  } catch (error) {
                    // The model should see a failed tool, not the loop die.
                    return {
                      error: error instanceof Error ? error.message : String(error),
                    };
                  }
                },
              }
            : {}),
        } as never);
      }
      return new mod.ToolLoopAgent({
        model: ai.model(reasoning === undefined ? {} : { reasoning }),
        ...(instructions ?? system ? { instructions: instructions ?? system } : {}),
        tools: wrappedTools as never,
        stopWhen: mod.stepCountIs(maxSteps ?? DEFAULT_MAX_STEPS),
        ...rest,
      } as never);
    },
  };

  return { ai, agent };
}
